'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { ScrollArea } from '../primitives/scroll-area';

type BaseProps = {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Modal title displayed in the header
   */
  title: string;
  /**
   * Optional subtitle displayed below the title in the header
   */
  subTitle?: string;
  /**
   * Callback when the modal is closed
   */
  onClose: () => void;
  /**
   * Array of action buttons for the footer
   */
  actions?: ReactNode[];
  /**
   * Additional class name for the modal container
   */
  className?: string;
  /**
   * Additional class name for the header section
   */
  headerClassName?: string;
  /**
   * Whether clicking the backdrop closes the modal (default: true)
   */
  closeOnBackdrop?: boolean;
  /**
   * Whether the modal body should use the internal scroll area (default: true)
   */
  withScrollArea?: boolean;
  /**
   * Whether to hide the close button in the header (default: false)
   */
  hideCloseButton?: boolean;
  /**
   * Modal width. Defaults to 'sm' (480px) to preserve existing behavior.
   * All sizes clamp to 95vw so narrow viewports don't overflow.
   * - sm: 480px — confirmations, short forms
   * - md: 600px — medium forms with moderate content
   * - lg: 720px — rich editors with multiple sections
   * - xl: 960px — data-heavy screens (previews, tables)
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

const sizeWidth = {
  sm: 'w-[min(480px,95vw)]',
  md: 'w-[min(600px,95vw)]',
  lg: 'w-[min(720px,95vw)]',
  xl: 'w-[min(960px,95vw)]',
} as const;

type DialogModalProps = BaseProps & {
  /**
   * Modal type - 'dialog' for simple dialogs with title, description, and actions
   */
  variant: 'dialog';
  /**
   * Description text displayed in the modal body
   */
  description: string;
  /**
   * Icon, children, and footerText are not allowed for dialog type
   */
  icon?: never;
  children?: never;
  footerText?: never;
};

type ContentModalProps = BaseProps & {
  /**
   * Modal type - 'content' for modals with custom content
   */
  variant?: 'content';
  /**
   * Icon component displayed next to the title
   */
  icon?: React.ComponentType<{ className?: string }>;
  /**
   * Modal content (passed as children)
   */
  children: ReactNode;
  /**
   * Optional text displayed in the footer (left side)
   */
  footerText?: ReactNode;
  /**
   * Description is not allowed for content type
   */
  description?: never;
};

type Props = DialogModalProps | ContentModalProps;

export const Modal = (props: Props) => {
  const {
    isOpen,
    title,
    subTitle,
    onClose,
    actions,
    className,
    headerClassName,
    variant = 'content',
    closeOnBackdrop = true,
    withScrollArea = true,
    hideCloseButton = false,
    size = 'sm',
  } = props;

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        {/* Overlay */}
        <DialogPrimitive.Close asChild>
          <div
            data-slot="modal-overlay"
            // `z-modal` (not `z-overlay`) so when a Modal opens on top of
            // another modal-tier surface its backdrop still covers it.
            // DOM order keeps Modal content above its own overlay.
            className="fixed inset-0 z-modal bg-primary-100/20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 cursor-default"
          />
        </DialogPrimitive.Close>

        {/* Content */}
        <DialogPrimitive.Content
          data-slot="modal-container"
          aria-describedby={undefined}
          onInteractOutside={(e) => {
            if (!closeOnBackdrop) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            if (!closeOnBackdrop) {
              e.preventDefault();
            }
          }}
          className={cn(
            // Animations
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            // Positioning
            'fixed top-1/2 left-1/2 z-modal -translate-x-1/2 -translate-y-1/2',
            // Layout — width controlled by `size` prop, height capped at
            // 90vh so content-driven modals can't push off-screen. Children
            // flex to fill that space (see withScrollArea={false} branch).
            'flex max-h-[90vh] flex-col overflow-hidden',
            sizeWidth[size],
            // Styling
            'rounded-2xl border border-primary-10 bg-white',
            'shadow-lg',
            // Focus
            'focus-visible:outline-none',
            className,
          )}
        >
          {variant === 'dialog' ? (
            <>
              {/* Dialog Header */}
              <div
                data-slot="modal-header"
                className={cn(
                  'flex shrink-0 flex-col overflow-hidden p-3 px-4 pb-2',
                  headerClassName,
                )}
              >
                <DialogPrimitive.Title className="text-body-13-bold font-semibold text-primary-100">
                  {title}
                </DialogPrimitive.Title>
                {subTitle && <span className="text-body-13 text-primary-50">{subTitle}</span>}
              </div>

              {/* Dialog Description */}
              <div data-slot="modal-content" className="flex flex-col gap-3 py-3 pt-0 px-4">
                <DialogPrimitive.Description className="text-body-14 text-primary-60">
                  {props.description}
                </DialogPrimitive.Description>
              </div>

              {/* Dialog Footer */}
              {actions && actions.length > 0 && (
                <div
                  data-slot="modal-footer"
                  className="flex shrink-0 items-center justify-end border-t border-primary-5 bg-white p-3"
                >
                  <div className="flex items-center gap-2">{actions}</div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Content Header */}
              <div
                data-slot="modal-header"
                className={cn(
                  'flex shrink-0 items-center justify-between overflow-hidden border-b border-primary-5 bg-white p-3',
                  headerClassName,
                )}
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    {props.icon && <props.icon className="text-m text-primary-100" />}
                    <DialogPrimitive.Title className="text-heading-16 leading-tight text-primary-100">
                      {title}
                    </DialogPrimitive.Title>
                  </div>
                  {subTitle && <span className="text-body-13 text-primary-50">{subTitle}</span>}
                </div>
                {!hideCloseButton && (
                  <DialogPrimitive.Close asChild>
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex size-6 cursor-pointer items-center justify-center rounded-md hover:bg-primary-5 focus:outline-none transition-colors"
                      aria-label="Close"
                    >
                      <XIcon className="size-4 text-primary-40" />
                    </button>
                  </DialogPrimitive.Close>
                )}
              </div>

              {/* Content Body */}
              {withScrollArea ? (
                <ScrollArea
                  data-slot="modal-content"
                  className="flex flex-col gap-3 px-3"
                  style={{ maxHeight: '400px' }}
                  hideHorizontal
                >
                  <div className="py-3">{props.children}</div>
                </ScrollArea>
              ) : (
                // `flex-1 min-h-0 overflow-y-auto` lets the body take the
                // remaining space in the flex column and scroll when it
                // exceeds the 90vh container. `min-h-0` is required or the
                // flex child would refuse to shrink below its intrinsic
                // content height and the overflow would never engage.
                <div
                  data-slot="modal-content"
                  className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3"
                >
                  {props.children}
                </div>
              )}

              {/* Content Footer */}
              {(actions?.length || props.footerText) && (
                <div
                  data-slot="modal-footer"
                  className="flex shrink-0 items-center justify-between border-t border-primary-5 bg-white p-3"
                >
                  <div className="text-body-12 text-primary-40">{props.footerText}</div>
                  {actions && actions.length > 0 && (
                    <div className="flex items-center gap-2">{actions}</div>
                  )}
                </div>
              )}
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
